import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, Clock } from "lucide-react";
import SEO from "@/components/site/SEO";
import CTASection from "@/components/site/CTASection";
import { POSTS } from "@/lib/blog";

export default function Blog() {
  const [featured, ...rest] = POSTS;

  return (
    <>
      <SEO
        title="Blog — L F Building & Maintenance Ltd"
        description="Straight-talking advice on extensions, loft conversions, roofing, kitchens and bathrooms from L F Building's site managers."
        path="/blog"
      />

      {/* HEADER */}
      <section className="bg-brand-ink text-white pt-28 md:pt-40 pb-20 md:pb-28 border-b border-white/10">
        <div className="container-lf grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-9 space-y-6">
            <span className="eyebrow text-brand">Blog &amp; Advice</span>
            <h1 className="font-display font-bold uppercase text-6xl md:text-8xl leading-[0.9]">
              Field <span className="text-brand">notes.</span>
            </h1>
            <p className="text-lg text-white/70 max-w-2xl">
              Practical, straight-talking articles from the L F Building team — on planning, pricing, and the questions
              we get asked on-site every week.
            </p>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-20 md:py-28 bg-white" data-testid="blog-featured">
        <div className="container-lf">
          <Link
            to={`/blog/${featured.slug}`}
            data-testid={`blog-featured-${featured.slug}`}
            className="group grid grid-cols-1 lg:grid-cols-12 gap-10 items-center border border-black/10 hover:border-brand transition-colors p-6 md:p-10"
          >
            <div className="lg:col-span-7 relative aspect-[16/10] overflow-hidden bg-neutral-100">
              <img src={featured.cover} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
              <div className="absolute top-4 left-4 bg-brand text-white px-3 py-1 text-xs font-bold uppercase tracking-widest">
                Featured
              </div>
            </div>
            <div className="lg:col-span-5 space-y-5">
              <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-neutral-500">
                <span className="text-brand font-semibold">{featured.tag}</span>
                <span>·</span>
                <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {featured.date}</span>
                <span>·</span>
                <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {featured.read}</span>
              </div>
              <h2 className="font-display font-bold uppercase text-4xl md:text-5xl leading-[0.95] group-hover:text-brand transition-colors">
                {featured.title}
              </h2>
              <p className="text-neutral-600 leading-relaxed">{featured.excerpt}</p>
              <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-brand pt-2">
                Read the article <ArrowUpRight className="w-4 h-4" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* GRID */}
      <section className="pb-24 md:pb-32 bg-white" data-testid="blog-grid">
        <div className="container-lf">
          <div className="mb-10 flex items-end justify-between">
            <h2 className="font-display font-bold uppercase text-3xl md:text-4xl">More from the site</h2>
            <span className="text-xs uppercase tracking-widest text-neutral-500">{rest.length} articles</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((p, i) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group border border-black/10 hover:border-brand transition-colors flex flex-col overflow-hidden"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
                  <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute top-4 left-4 bg-white text-brand-ink px-3 py-1 text-xs font-bold uppercase tracking-widest">
                    {p.tag}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-neutral-500 mb-3">
                    <span>{p.date}</span>
                    <span>·</span>
                    <span>{p.read}</span>
                  </div>
                  <h3 className="font-display font-bold uppercase text-2xl leading-tight group-hover:text-brand transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-sm text-neutral-600 mt-3 leading-relaxed flex-1">{p.excerpt}</p>
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand mt-5">
                    Read more <ArrowUpRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection eyebrow="Have a project?" title="Let's talk it through." />
    </>
  );
}
